import { useMemo } from 'react';
import { DescriptorType, FeatureFlags } from '@fabric-ui/components';
import { DeliveryMethodOptions } from '@fabric-ui/components/Table';
import { QueryKey } from '@fabric-ui/utils';
import { useQuery } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { useAvContext } from '../../context/AvContextProvider';
import { CalcDimType, parseCalcDimType } from '../../context/useCalcDims';
import { serializeSortItem, sortByStringField } from '../../utils/utils';
import { getSimplifiedExpression } from '../CustomDashboards/CustomDashboardsUtils/expressionUtils';
import { DwQueryRequest, SortDir } from '../CustomDashboards/types';
import { convertValueToURI, emailIntegrationType } from './reports.utils';

export const useMeasurementsList = ({
  entityName,
  filterFunc = v => v.expression && v.context.includes(entityName),
  onSuccess = d => d,
  enabled = true,
}: {
  entityName?: string;
  filterFunc?: (v: any) => any;
  onSuccess?: (d) => any;
  enabled?: boolean;
}) => {
  const { measurements: measurementsRaw = { visible: [] } } = useAvContext();
  return useMemo(
    () => onSuccess({ data: enabled ? sortByStringField(measurementsRaw.visible.filter(filterFunc), 'displayName') : [] }),
    [entityName, filterFunc, enabled]
  );
};

export const getCalcDimType = (type: CalcDimType) => {
  const kind = parseCalcDimType(type);
  return kind ? DescriptorType[kind] : kind;
};

export const mapCalcDimToFieldOption = ({ systemName, displayName, type }) => ({
  value: systemName,
  pathAlias: systemName,
  title: displayName,
  group: 'Calculated Dimensions',
  entityTypeId: { builtin: true },
  type: getCalcDimType(type),
});

export const useCalculatedDimensionsList = ({
  projectionName,
  filterFunc = v => v.context?.some(ctx => ctx.name === projectionName),
  onSuccess = d => d,
  enabled = true,
}: {
  projectionName?: string;
  filterFunc?: (v: any) => any;
  onSuccess?: (d) => any;
  enabled?: boolean;
}) => {
  const { calculatedDimensions: calculatedDimensionsRaw = { visible: [] }, featureFlags } = useAvContext();
  const isEnabled = enabled && featureFlags[FeatureFlags.CalculatedDimensions];
  return useMemo(
    () => onSuccess({ data: isEnabled ? sortByStringField(calculatedDimensionsRaw.visible.filter(filterFunc), 'displayName') : [] }),
    [projectionName, filterFunc, isEnabled]
  );
};

export const useBuildQueryParams = ({ headCellsIds, measurements }) =>
  headCellsIds.map(headCell => {
    const measurement = measurements.find(({ systemName }) => systemName === headCell);
    if (!measurement) {
      return headCell;
    }
    return { systemName: measurement.systemName, alias: headCell };
  });

export const useBuildQueryParamsObject = ({ headCellsMeasurements, measurements }) =>
  headCellsMeasurements.reduce((acc, headCell) => {
    const measurement = measurements.find(({ systemName }) => systemName === headCell);
    if (!measurement) {
      acc[headCell] = headCell;
    } else {
      acc[headCell] = measurement.systemName;
    }
    return acc;
  }, {});

export const useAvailableIntegrationInstance = (typeOptions = Object.values(DeliveryMethodOptions), enabled = true) => {
  const { api } = useAvContext();
  return useQuery({
    queryKey: [QueryKey.AvailableIntegrationInstance],
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      api(GET_INTEGRATION, {
        signal,
        onSuccess: ({ data }) =>
          [emailIntegrationType, ...data.findAvailableIntegrationInstanceForReports].filter(({ integrationType }) =>
            typeOptions.includes(integrationType)
          ),
      }),
    enabled,
    gcTime: 0,
  });
};

export const getSearchParamsFromQueryObject = (queryObject: DwQueryRequest) => {
  const expressions = queryObject.select.dims
    .filter(dim => dim.expression)
    .reduce((acc, dim) => ({ ...acc, ...getSimplifiedExpression(dim.name, dim.expression!) }), {});

  return {
    headCells: [...queryObject.select.dims.map(({ name }) => name), ...queryObject.select.metrics.map(({ name, alias }) => alias || name)],
    orderBy: queryObject?.sorting?.[0]
      ? [serializeSortItem({ property: queryObject.sorting[0].name, isAsc: queryObject.sorting[0].dir === SortDir.ASC })]
      : undefined,
    activeProjName: queryObject.projectionId.name,
    isCurrentTime: !queryObject?.timeRange,
    ...(queryObject?.timeRange ? { timeRange: convertValueToURI(queryObject?.timeRange) } : {}),
    ...(Object.keys(expressions) ? { expressions } : {}),
  };
};

export const GET_INTEGRATION = gql`
  query findAvailableIntegrationInstanceForReports {
    findAvailableIntegrationInstanceForReports {
      id
      name
      integrationType
      active
      redactedConfig
    }
  }
`;
